package meineglasfaser.frontend.www.pages.portal

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import kotlinx.datetime.*
import meineglasfaser.api.Customer
import meineglasfaser.api.CustomerType
import meineglasfaser.api.InvoiceDeliveryMode
import meineglasfaser.api.routes.CustomerPortalRoutes
import meineglasfaser.api.routes.CustomerPortalRoutes.UpdateData.*
import meineglasfaser.api.ubl.UBLPostalAddress
import meineglasfaser.frontend.common.BackendApi
import meineglasfaser.frontend.common.components.widgets.Toast
import meineglasfaser.frontend.common.components.widgets.Toasts
import meineglasfaser.frontend.common.components.widgets.UBLPostalAddressInputs
import meineglasfaser.frontend.common.components.widgets.rememberQuery
import meineglasfaser.frontend.common.components.yaform.*
import meineglasfaser.frontend.common.setJsonBody
import meineglasfaser.frontend.common.withAuth
import meineglasfaser.frontend.www.components.CustomerState
import meineglasfaser.frontend.www.components.GlobalCustomerState
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.dom.Article
import org.jetbrains.compose.web.dom.Header
import org.jetbrains.compose.web.dom.Text
import kotlin.time.Clock

@Page
@Layout(".components.layouts.PortalLayout")
@Composable
fun MyDataPage() {
    val fullInfo = rememberQuery<Customer?>(null) {
        BackendApi.get(CustomerPortalRoutes.Account.FullInfo()) {
            withAuth()
        }
    }

    val info = fullInfo ?: return

    if (info.type == CustomerType.INDIVIDUAL) {
        YaForm(
            initial = Individual.RequestBody(
                firstName = info.individualFirstName ?: "",
                lastName = info.individualLastName ?: "",
                dob = info.individualDob,
            ),
            onSubmitScope = { data ->
                val body = Individual.RequestBody(
                    firstName = data.string(Individual.RequestBody::firstName),
                    lastName = data.string(Individual.RequestBody::lastName),
                    dob = data.localDateOrNull(Individual.RequestBody::dob),
                )
                BackendApi.post(Individual()) {
                    withAuth()
                    setJsonBody(body)
                }.fold(
                    onSuccess = {
                        Toasts.display(Toast.success("Persönliche Daten erfolgreich aktualisiert."))
                        GlobalCustomerState.value = CustomerState.Indeterminate
                    },
                    onFailure = { error ->
                        Toasts.display(Toast.error("Fehler beim Aktualisieren der persönlichen Daten: ${error.message}"))
                    }
                )
            }
        ) {
            Article {
                Header { Text("Persönliche Daten") }
                YaFormTextInput(
                    prop = Individual.RequestBody::firstName,
                    type = InputType.Text,
                    label = { Text("Vorname") },
                    required = true,
                )
                YaFormTextInput(
                    prop = Individual.RequestBody::lastName,
                    type = InputType.Text,
                    label = { Text("Nachname") },
                    required = true,
                )
                YaFormLocalDateInput(
                    prop = Individual.RequestBody::dob,
                    label = { Text("Geburtsdatum") },
                    min = LocalDate(1900, 1, 1),
                    max = Clock.System.todayIn(TimeZone.currentSystemDefault())
                        .minus(DatePeriod(years = 18)),
                    required = false,
                )
                YaFormButton(ButtonType.Submit) { Text("Speichern") }
            }
        }
    }

    if (info.type == CustomerType.COMPANY) {
        YaForm(
            initial = Company.RequestBody(
                companyName = info.companyName ?: "",
                companyVatNumber = info.companyVatNumber ?: "",
                companyContactPerson = info.companyContactPerson ?: "",
            ),
            onSubmitScope = { data ->
                val body = Company.RequestBody(
                    companyName = data.string(Company.RequestBody::companyName),
                    companyVatNumber = data.string(Company.RequestBody::companyVatNumber),
                    companyContactPerson = data.string(Company.RequestBody::companyContactPerson),
                )
                BackendApi.post(Company()) {
                    withAuth()
                    setJsonBody(body)
                }.fold(
                    onSuccess = {
                        Toasts.display(Toast.success("Firmendaten erfolgreich aktualisiert."))
                        GlobalCustomerState.value = CustomerState.Indeterminate
                    },
                    onFailure = { error ->
                        Toasts.display(Toast.error("Fehler beim Aktualisieren der Firmendaten: ${error.message}"))
                    }
                )
            }
        ) {
            Article {
                Header { Text("Firmendaten ändern") }
                YaFormTextInput(
                    prop = Company.RequestBody::companyName,
                    label = { Text("Firmenname") },
                    type = InputType.Text,
                    required = true
                )
                YaFormTextInput(
                    prop = Company.RequestBody::companyVatNumber,
                    label = { Text("UID-Nr.") },
                    type = InputType.Text,
                    pattern = "ATU[0-9]{8}",
                    required = true
                )
                YaFormTextInput(
                    prop = Company.RequestBody::companyContactPerson,
                    label = { Text("Ansprechperson") },
                    type = InputType.Text,
                    required = false
                )
                YaFormButton(ButtonType.Submit) { Text("Speichern") }
            }
        }
    }

    YaForm(
        initial = Phone.RequestBody(
            telFixedLine = info.telFixedLine ?: "",
            telMobile = info.telMobile ?: "",
        ),
        onSubmitScope = { data ->
            val body = Phone.RequestBody(
                telFixedLine = data.string(Phone.RequestBody::telFixedLine),
                telMobile = data.string(Phone.RequestBody::telMobile),
            )
            BackendApi.post(Phone()) {
                withAuth()
                setJsonBody(body)
            }.fold(
                onSuccess = {
                    Toasts.display(Toast.success("Telefonnummern erfolgreich aktualisiert."))
                    GlobalCustomerState.value = CustomerState.Indeterminate
                },
                onFailure = { error ->
                    Toasts.display(Toast.error("Fehler beim Aktualisieren der Telefonnummern: ${error.message}"))
                }
            )
        }
    ) {
        Article {
            Header { Text("Telefonnummern") }
            YaFormTextInput(
                prop = Phone.RequestBody::telFixedLine,
                label = { Text("Festnetz") },
                type = InputType.Tel,
                required = false,
            )
            YaFormTextInput(
                prop = Phone.RequestBody::telMobile,
                label = { Text("Mobil") },
                type = InputType.Tel,
                required = false,
            )
            YaFormButton(ButtonType.Submit) { Text("Speichern") }
        }
    }

    YaForm(
        initial = info.invoicingAddress ?: UBLPostalAddress.EMPTY_AUSTRIA,
        onSubmitScope = { data ->
            val address = UBLPostalAddress(
                streetName = data.string(UBLPostalAddress::streetName),
                cityName = data.string(UBLPostalAddress::cityName),
                postalCode = data.string(UBLPostalAddress::postalCode),
                countryCode = data.string(UBLPostalAddress::countryCode),
            )
            BackendApi.post(CustomerPortalRoutes.UpdateData.InvoicingAddress()) {
                withAuth()
                setJsonBody(address)
            }.fold(
                onSuccess = {
                    Toasts.display(Toast.success("Rechnungsadresse erfolgreich aktualisiert."))
                    GlobalCustomerState.value = CustomerState.Indeterminate
                },
                onFailure = { error ->
                    Toasts.display(Toast.error("Fehler beim Aktualisieren der Rechnungsadresse: ${error.message}"))
                }
            )
        }
    ) {
        Article {
            Header { Text("Rechnungsadresse") }
            UBLPostalAddressInputs(
                streetName = UBLPostalAddress::streetName,
                cityName = UBLPostalAddress::cityName,
                postalZone = UBLPostalAddress::postalCode,
                countryCode = UBLPostalAddress::countryCode,
            )
            YaFormButton(ButtonType.Submit) { Text("Speichern") }
        }
    }

    YaForm(
        initial = Invoicing.RequestBody(
            invoicingEmail = info.invoicingEmail ?: "",
            invoicingDeliveryMode = info.invoicingDeliveryMode,
            invoicingAccountHolder = info.invoicingAccountHolder ?: "",
            invoicingIBAN = info.invoicingIBAN ?: "",
        ),
        onSubmitScope = { data ->
            val body = Invoicing.RequestBody(
                invoicingEmail = data.string(Invoicing.RequestBody::invoicingEmail),
                invoicingDeliveryMode = InvoiceDeliveryMode.valueOf(
                    data.string(Invoicing.RequestBody::invoicingDeliveryMode)
                ),
                invoicingAccountHolder = data.string(Invoicing.RequestBody::invoicingAccountHolder),
                invoicingIBAN = data.string(Invoicing.RequestBody::invoicingIBAN),
            )
            BackendApi.post(Invoicing()) {
                withAuth()
                setJsonBody(body)
            }.fold(
                onSuccess = {
                    Toasts.display(Toast.success("Rechnungsdaten erfolgreich aktualisiert."))
                    GlobalCustomerState.value = CustomerState.Indeterminate
                },
                onFailure = { error ->
                    Toasts.display(Toast.error("Fehler beim Aktualisieren der Rechnungsdaten: ${error.message}"))
                }
            )
        }
    ) {
        Article {
            Header { Text("Rechnungsdaten") }
            YaFormTextInput(
                prop = Invoicing.RequestBody::invoicingEmail,
                label = { Text("Rechnungs-E-Mail") },
                type = InputType.Email,
                required = true,
            )
            YaFormDropdown(
                prop = Invoicing.RequestBody::invoicingDeliveryMode,
                label = { Text("Rechnungszustellung") },
                options = listOf(
                    InvoiceDeliveryMode.EMAIL to "Per E-Mail (PDF)",
                    InvoiceDeliveryMode.POSTAL to "Per Post (Papier)",
                ),
                required = true,
                disabled = true,
            )
            YaFormTextInput(
                prop = Invoicing.RequestBody::invoicingAccountHolder,
                label = { Text("Kontoinhaber") },
                type = InputType.Text,
                required = true,
            )
            YaFormTextInput(
                prop = Invoicing.RequestBody::invoicingIBAN,
                label = { Text("IBAN") },
                type = InputType.Text,
                pattern = "AT[0-9]{2}[0-9]{16}",
                required = true,
            )
            YaFormButton(ButtonType.Submit) { Text("Speichern") }
        }
    }
}
